import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Truck } from "lucide-react";
import { EquipmentSection } from "@/components/EquipmentSection";
import ReplacementValueInput from "@/components/ReplacementValueInput";

/**
 * Phase AE — Assign Equipment dialog.
 *
 * Mounted from the AdminSubmissionDetailPage on an APPROVED submission.
 * Captures the equipment fields (via the shared <EquipmentSection>) +
 * billing period & rates, then POSTs to
 * ``/admin/submissions/{id}/assign-equipment`` which atomically
 * creates a pending_contract rental + draft contract.
 *
 * The dialog deliberately does NOT collect:
 *   - start_date / end_date (deferred until contract generation)
 *   - replacement value, deposit, minimum term (collected at gen time)
 *   - template id (picked when the admin clicks the "Pending Contract"
 *     row on the customer's Contracts tab)
 *
 * Props:
 *   open                   bool
 *   onOpenChange(open)     parent's setter
 *   submissionId           string
 *   customerName           string — used in the success toast copy
 *   defaultEquipmentType   string — pulled from submission.data.equipment_type
 *   onSuccess({rental_id, contract_id, customer_id})  refresh hook
 */

const EMPTY_FORM = {
    equipment_type: "reefer",
    equipment_type_other: "",
    unit_number: "",
    vin: "",
    make: "",
    model: "",
    year: "",
    plate: "",
    equipment_size: "",
    billing_period: "monthly",
    daily_rate: "",
    monthly_rate: "",
    // Phase RV — replacement value, optional at assignment time.
    value: null,
    // Task 28-F — security deposit (dollars as a string for the input).
    // Pre-fills from the tenant default_deposits map when the admin
    // picks an equipment type. Soft warning (no hard block) when
    // blank/zero AND a default exists. Hard gate happens later at
    // contract-send time (Task 17).
    deposit: "",
    alvys_trailer_id: "",
    alvys_synced_at: "",
};

const EQUIPMENT_TYPE_WHITELIST = new Set([
    "reefer", "dry_van", "conestoga", "flatbed", "step_deck", "other",
]);

export default function AssignEquipmentDialog({
    open,
    onOpenChange,
    submissionId,
    customerName,
    defaultEquipmentType,
    onSuccess,
}) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Task 28-F — tenant default deposits map (equipment_type slug → cents).
    // Fetched once when the dialog opens; failure is silent (the soft
    // warning simply won't show — no hard dependency).
    const [defaultDeposits, setDefaultDeposits] = useState({});

    // Reset whenever the dialog re-opens. Equipment type seeds from the
    // submission's existing intake value when it's one of our enum
    // values; otherwise falls back to ``reefer``.
    useEffect(() => {
        if (!open) return;
        const seedEq =
            defaultEquipmentType && EQUIPMENT_TYPE_WHITELIST.has(defaultEquipmentType)
                ? defaultEquipmentType
                : "reefer";
        setForm({ ...EMPTY_FORM, equipment_type: seedEq });
        setError(null);
        setSubmitting(false);
        // Fetch tenant default deposits + pre-fill if a default exists
        // for the seeded equipment type. Best-effort.
        (async () => {
            try {
                const { data } = await api.get("/admin/settings/default-deposits");
                const defaults = data?.defaults || {};
                setDefaultDeposits(defaults);
                const seedCents = Number(defaults?.[seedEq] || 0);
                if (seedCents > 0) {
                    setForm((f) => ({ ...f, deposit: String(seedCents / 100) }));
                }
            } catch {
                /* silent — soft warning still works without defaults */
            }
        })();
    }, [open, defaultEquipmentType]);

    // Task 28-F — when the admin changes the equipment type INSIDE the
    // dialog, re-seed the deposit field IF it was empty (don't clobber
    // a manually-entered value).
    useEffect(() => {
        if (!open) return;
        if (form.deposit) return;
        const cents = Number(defaultDeposits?.[form.equipment_type] || 0);
        if (cents > 0) {
            setForm((f) => ({ ...f, deposit: String(cents / 100) }));
        }
    }, [form.equipment_type, defaultDeposits, open]);

    const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const canSubmit = (() => {
        if (submitting) return false;
        if (!form.equipment_type) return false;
        if (form.equipment_type === "other" && !form.equipment_type_other.trim()) return false;
        if (!form.unit_number.trim()) return false;
        return true;
    })();

    const onSave = async () => {
        if (!canSubmit) return;
        setError(null);
        setSubmitting(true);
        try {
            // Build a tight payload — drop blanks so the backend doesn't
            // see ``""`` for optional numeric/string fields.
            const payload = {
                equipment_type: form.equipment_type,
                unit_number: form.unit_number.trim(),
            };
            if (form.equipment_type === "other" && form.equipment_type_other.trim()) {
                payload.equipment_type_other = form.equipment_type_other.trim();
            }
            for (const k of ["vin", "make", "model", "plate", "equipment_size",
                             "billing_period", "alvys_trailer_id", "alvys_synced_at"]) {
                if (form[k] && String(form[k]).trim() !== "") payload[k] = String(form[k]).trim();
            }
            if (form.year && String(form.year).trim() !== "") {
                const y = parseInt(form.year, 10);
                if (!Number.isNaN(y)) payload.year = y;
            }
            for (const k of ["daily_rate", "monthly_rate"]) {
                if (form[k] && String(form[k]).trim() !== "") {
                    const n = parseFloat(form[k]);
                    if (!Number.isNaN(n)) payload[k] = n;
                }
            }
            // Phase RV — pass replacement value when set. The shared
            // ReplacementValueInput already rounds to int dollars and
            // returns null for blank/zero — the server's gate fires
            // later at COI-request send time.
            if (form.value != null && form.value > 0) payload.value = form.value;
            // Task 28-F — deposit (dollars in the input → cents on the wire).
            // Soft-warning UX: admin can save with empty/zero. Skip the
            // field on the wire when blank/zero so the BE leaves the
            // rental's deposit subdoc at its empty default.
            const depNum = Number(form.deposit);
            if (form.deposit && Number.isFinite(depNum) && depNum > 0) {
                payload.deposit_amount_cents = Math.round(depNum * 100);
            }

            const { data } = await api.post(
                `/admin/submissions/${submissionId}/assign-equipment`,
                payload,
            );
            toast.success(
                `Equipment assigned. Pending contract created in ${customerName || "customer"}'s Contracts.`,
            );
            onOpenChange(false);
            if (typeof onSuccess === "function") onSuccess(data);
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            toast.error(msg || "Failed to assign equipment");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="assign-equipment-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Truck size={18} className="text-blue-900" />
                        Assign Equipment
                    </DialogTitle>
                    <DialogDescription>
                        Pick the unit you're assigning to this customer. We'll create a draft rental
                        and a pending contract you can generate later from the Contracts tab.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <section className="space-y-3">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                            A · Equipment
                        </h3>
                        <EquipmentSection
                            value={form}
                            onChange={updateField}
                            open={open}
                            testIdPrefix="ae-"
                        />
                    </section>

                    <section className="space-y-3">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                            B · Billing (optional)
                        </h3>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <Label>Billing period</Label>
                                <select
                                    value={form.billing_period}
                                    onChange={(e) => updateField("billing_period", e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                                    data-testid="ae-billing-period"
                                >
                                    <option value="">— auto —</option>
                                    <option value="daily">Daily</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Daily rate ($)</Label>
                                <Input
                                    type="number" step="0.01"
                                    value={form.daily_rate}
                                    onChange={(e) => updateField("daily_rate", e.target.value)}
                                    data-testid="ae-daily-rate"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Monthly rate ($)</Label>
                                <Input
                                    type="number" step="0.01"
                                    value={form.monthly_rate}
                                    onChange={(e) => updateField("monthly_rate", e.target.value)}
                                    data-testid="ae-monthly-rate"
                                />
                            </div>
                        </div>
                        {/* Phase RV — equipment replacement value. Optional
                            here, but required before any COI request can
                            be sent for the resulting rental. */}
                        <ReplacementValueInput
                            value={form.value}
                            onChange={(v) => updateField("value", v)}
                            testIdBase="ae-replacement-value"
                        />
                        {/* Task 28-F — security deposit. Soft-warning at
                            assignment; the Task 17 hard-gate fires later
                            at contract-send time. */}
                        <div className="space-y-1.5">
                            <Label htmlFor="ae-deposit">Security deposit ($)</Label>
                            <Input
                                id="ae-deposit"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.deposit}
                                onChange={(e) => updateField("deposit", e.target.value)}
                                data-testid="ae-deposit"
                            />
                            <p className="text-xs text-slate-500">
                                Optional at assignment; required before sending the contract to the customer.
                            </p>
                            {(() => {
                                const slug = form.equipment_type;
                                const depNum = Number(form.deposit);
                                const isZeroOrEmpty =
                                    !form.deposit ||
                                    !Number.isFinite(depNum) ||
                                    depNum <= 0;
                                const defaultCents = slug ? Number(defaultDeposits?.[slug] || 0) : 0;
                                if (!isZeroOrEmpty || !defaultCents) return null;
                                const dollars = (defaultCents / 100).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                });
                                return (
                                    <div
                                        className="mt-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                                        data-testid="ae-deposit-warning-banner"
                                    >
                                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p>
                                                No deposit set. The default for{" "}
                                                <strong>{slug.replace(/_/g, " ")}</strong>{" "}
                                                is <strong>{dollars}</strong>.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => updateField("deposit", String(defaultCents / 100))}
                                            className="rounded bg-amber-900 text-white text-[11px] font-semibold px-2 py-1 hover:bg-amber-800"
                                            data-testid="ae-deposit-use-default"
                                        >
                                            Use default
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    </section>

                    {error && (
                        <div
                            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                            data-testid="ae-error"
                        >
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="ae-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={onSave}
                        disabled={!canSubmit}
                        data-testid="ae-save-btn"
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                    >
                        {submitting ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" />
                                Saving…
                            </span>
                        ) : (
                            "Save & create pending contract"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
